/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.closest('form');
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('[name="filter.p.product_type"]');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.addListeners();
      this.init();
    }

    static toPrefixQuery(value) {
      const query = String(value || '').trim();
      if (!query || /["'“”‘’:*()\\<>=\[\]{}]/u.test(query)
        || /(?:^|\s)(?:AND|OR|NOT)(?=\s|$)/iu.test(query)
        || /(?:^|\s)[+-]\S/u.test(query)) return query;
      const words = query.normalize('NFKC').match(/[\p{L}\p{N}]+/gu);
      if (!words?.length) return query;

      const { aliases, canonical } = SearchForm.getAliasMappings();
      const normalizedWords = words.map(SearchForm.normalizeAlias);
      const translatedWords = [];
      for (let index = 0; index < words.length;) {
        const canonicalPhrase = canonical.find((phrase) => phrase.every(
          (word, offset) => word === normalizedWords[index + offset]
        ));
        if (canonicalPhrase) {
          translatedWords.push(...words.slice(index, index + canonicalPhrase.length));
          index += canonicalPhrase.length;
          continue;
        }

        let matches = aliases.filter((alias) => alias.words.every((word, offset) => {
          const inputWord = normalizedWords[index + offset];
          return word === inputWord || (offset === alias.words.length - 1
            && inputWord?.length >= 4 && word.startsWith(inputWord));
        }));

        if (matches.length) {
          const length = matches[0].words.length;
          matches = matches.filter((alias) => alias.words.length === length);
          const exactMatches = matches.filter((alias) => alias.words[length - 1]
            === normalizedWords[index + length - 1]);
          if (exactMatches.length) matches = exactMatches;
          const targets = new Set(matches.map((alias) => alias.target));
          const [target] = targets;
          translatedWords.push(...(targets.size === 1 && target
            ? target.split(' ') : words.slice(index, index + length)));
          index += length;
        } else {
          translatedWords.push(words[index]);
          index += 1;
        }
      }

      return translatedWords.map((word) => `${word}*`).join(' ');
    }

    static normalizeAlias(value) {
      return value.normalize('NFKC').toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
    }

    static getAliasMappings() {
      const source = theme.settings.searchAliasesEnabled
        && typeof theme.settings.searchAliases === 'string' ? theme.settings.searchAliases : '';
      if (SearchForm.aliasCache?.source === source) return SearchForm.aliasCache;

      const mappings = new Map();
      const canonical = new Set();
      source.split(/[\r\n]+/u).forEach((line) => {
        const parts = line.split('=');
        if (parts.length !== 2) return;
        const target = parts[0].trim().toLowerCase().replace(/\s+/gu, ' ');
        const aliases = parts[1].split(',').map((alias) => alias.trim().normalize('NFKC')
          .toLowerCase().replace(/\s+/gu, ' '));
        if (!/^[a-z]+(?: [a-z]+)*$/u.test(target)
          || aliases.some((alias) => !/^[a-zäöüß]+(?: [a-zäöüß]+)*$/u.test(alias))) return;

        canonical.add(target);
        aliases.forEach((alias) => {
          const normalized = SearchForm.normalizeAlias(alias);
          mappings.set(normalized, mappings.has(normalized) && mappings.get(normalized) !== target
            ? null : target);
        });
      });

      SearchForm.aliasCache = {
        source,
        aliases: Array.from(mappings, ([alias, target]) => ({ words: alias.split(' '), target }))
          .sort((a, b) => b.words.length - a.words.length),
        canonical: Array.from(canonical, (phrase) => phrase.split(' '))
          .sort((a, b) => b.length - a.length)
      };
      return SearchForm.aliasCache;
    }

    static queryKey(value) {
      return String(value || '').trim().normalize('NFKC').toLowerCase();
    }

    static getOriginalQuery(searchParams) {
      const original = searchParams.get('q_original')?.trim() || '';
      if (!original || searchParams.getAll('q').length !== 1
        || searchParams.getAll('q_original').length !== 1) return '';
      return SearchForm.queryKey(SearchForm.toPrefixQuery(original))
        === SearchForm.queryKey(searchParams.get('q')) ? original : '';
    }

    static preserveOriginalQuery(searchParams) {
      const params = new URLSearchParams(searchParams);
      const current = new URLSearchParams(window.location.search);
      const original = SearchForm.getOriginalQuery(params)
        || (params.getAll('q').length === 1
          && SearchForm.queryKey(params.get('q')) === SearchForm.queryKey(current.get('q'))
          ? SearchForm.getOriginalQuery(current) : '');
      params.delete('q_original');
      if (original) params.set('q_original', original);
      return params.toString();
    }

    static updatePaginationLinks() {
      const current = new URL(window.location.href);
      const original = SearchForm.getOriginalQuery(current.searchParams);
      document.querySelectorAll('#filter-results .js-pagination a[href]').forEach((link) => {
        let url;
        try {
          url = new URL(link.getAttribute('href'), current);
        } catch (error) {
          return;
        }
        if (url.origin !== current.origin || url.pathname !== current.pathname
          || url.searchParams.getAll('q').length !== 1
          || SearchForm.queryKey(url.searchParams.get('q'))
            !== SearchForm.queryKey(current.searchParams.get('q'))) return;
        const previous = url.search;
        url.searchParams.delete('q_original');
        if (original) url.searchParams.set('q_original', original);
        if (url.search !== previous) link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      });
    }

    static restoreSearchContext() {
      const inputs = document.querySelectorAll('[data-persist-search="true"]');
      if (!inputs.length && !document.querySelector('title[data-search-title-prefix]')) return;
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q') || '';
      const displayQuery = SearchForm.getOriginalQuery(params) || query.replace(/\*/g, '');
      const contextKey = `${SearchForm.queryKey(query)}\n${displayQuery}`;
      SearchForm.inputContexts = SearchForm.inputContexts || new WeakMap();
      inputs.forEach((input) => {
        const previous = SearchForm.inputContexts.get(input);
        if (!previous || previous.contextKey !== contextKey || input.value === previous.value) {
          input.value = displayQuery;
          input.setAttribute('value', displayQuery);
          input.classList.toggle('search__input--dirty', Boolean(displayQuery));
          SearchForm.inputContexts.set(input, { contextKey, value: displayQuery });
        }
      });
      document.querySelectorAll('[data-search-terms-text]').forEach((element) => {
        if (!element.closest('.js-search-results')) element.textContent = displayQuery;
      });
      document.querySelectorAll('[data-search-title-prefix][data-search-title-suffix]')
        .forEach((element) => {
          const prefix = displayQuery ? element.dataset.searchTitlePrefix
            : element.dataset.searchTitlePrefix.replace(/[:\s]+$/u, '');
          element.textContent = `${prefix}${displayQuery}${element.dataset.searchTitleSuffix}`;
        });
      SearchForm.updatePaginationLinks();

      const results = document.getElementById('filter-results');
      if (results && SearchForm.paginationResults !== results) {
        SearchForm.paginationObserver?.disconnect();
        SearchForm.paginationResults = results;
        SearchForm.paginationObserver = new MutationObserver(SearchForm.updatePaginationLinks);
        SearchForm.paginationObserver.observe(results, { childList: true, subtree: true });
      }
    }

    handleFormData(evt) {
      const query = evt.formData.get('q');
      evt.formData.delete('q_original');
      if (typeof query === 'string') {
        evt.formData.set('q', SearchForm.toPrefixQuery(query));
        if (query.trim()) evt.formData.set('q_original', query.trim());
      }
    }

    addListeners() {
      this.form?.addEventListener('formdata', this.handleFormData.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }
  }

  customElements.define('search-form', SearchForm);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', SearchForm.restoreSearchContext, { once: true });
  } else {
    SearchForm.restoreSearchContext();
  }
  document.addEventListener('on:facet-filters:updated', SearchForm.restoreSearchContext);
  document.addEventListener('on:bfcache:load-restore', SearchForm.restoreSearchContext);
  window.addEventListener('popstate', SearchForm.restoreSearchContext);
}
